<template>
  <rt-tooltip :content="String(currentValue)" :disabled="!isTip" placement="bottom">
    <div :class="[
      resizable ? '' : 'rt-input-resize',
      type === 'textarea' ? 'rt-xpc-textarea' : '',
      showWordCount || showWordLimit ? 'rt-xpc-textarea-count' : ''
    ]" class="rt-input" @mouseenter="inputHovering = true" @mouseleave="inputHovering = false">
      <el-input ref="input" :value="currentValue" v-bind="$attrs" :type="type" :clearable="false"
        :placeholder="computedInputPlaceholder" :readonly="readonly" :disabled="computedDisabled" :autosize="autosize"
        :show-password="showPassword" :maxlength="maxLength" :show-word-limit="showWordLimit"
        :show-word-count="showWordCount" :clear-icon-right="clearIconRight" :input-hovering="inputHovering"
        :show-close="showClose" @input="inputHandler" @focus="focus" @blur="blurHandler" @change="changeHandler">
        <slot />
        <!-- 前置内容 -->
        <slot slot="prepend" name="prepend">
        </slot>
        <!-- 后置元素 -->
        <slot slot="append" name="append">
        </slot>
        <slot slot="prefix" name="prefix">
        </slot>
        <slot slot="textareaSuffix" name="textareaSuffix">
        </slot>
      </el-input>
      <rt-icon v-if="_computedRecordIcon" slot="suffix" :color="SCSS_VARIABLES.COLOR_INFO" name="rt-base-record-lined"
        class="rt-xpc-input-suffix" @click="inputFocus" />
      <rt-icon v-if="showClose" slot="suffix" :name="computedIconName" :style="computedClearIconStyle"
        class="rt-xpc-input-clearicon" @click="clear" />
    </div>
  </rt-tooltip>
</template>

<script>
import inputFocusMixins from './inputFocusMixins.js';
import RtIcon from '../icon/rtIcon.vue';
import xpcConfig from 'vue-xpc/src/config';
import ElInput from 'vue-xpc/src/element-ui/packages/input';
import Placeholder from './placeholder.js';

export default {
  name: 'RtXpcInput',
  components: { RtIcon, ElInput },
  mixins: [inputFocusMixins, Placeholder],

  props: {
    value: [String, Number],
    datafieldname: String,
    showPassword: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    autosize: {
      type: [Boolean, Object],
      default: function () {
        return { maxRows: 3 };
      }
    },
    resizable: {
      type: Boolean,
      default: true
    },
    // 是否显示编辑icon图标
    showSuffixIcon: {
      type: Boolean,
      default: false
    },
    clearable: { type: Boolean, default: undefined },
    // 自定义清空按钮图标
    clearIconName: {
      type: String,
      default: undefined
    },
    // 自定义清空按钮位置
    clearIconRight: {
      type: Number,
      default: 0
    },
    // 显示统计字数
    showWordCount: {
      type: Boolean,
      default: false
    },
    // 显示限制字数
    showWordLimit: {
      type: Boolean,
      default: false
    },
    // 限制字数，默认为 undefined
    maxLength: {
      type: Number,
      default: Infinity
    },
    debounce: {
      type: Number,
      default: 0
    },
    autoTrim: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentValue: this.value,
      inputHovering: false,
      isTip: false
    };
  },
  inject: {
    elForm: {
      default: ''
    },
    rtFormItem: {
      default: ''
    }
  },
  computed: {
    /**
     * 右侧编辑按钮是否显示
     */
    _computedRecordIcon() {
      return (
        this.showSuffixIcon &&
        !this.computedDisabled &&
        !this.showPassword &&
        !this.showWordCount &&
        !this.showWordLimit &&
        this._computedSuffixIcon &&
        this.type !== 'textarea'
      );
    },
    /**
     * 当清空按钮设置偏移量的时候，显示后缀图标
     */
    _computedSuffixIcon() {
      return (
        (this.clearIconRight || xpcConfig.get('clearicon.clearIconRight')) >=
        24 || !this.showClose
      );
    },
    computedClearable() {
      return this.clearable === undefined
        ? this.elForm &&
        this.elForm.$attrs &&
        this.elForm.$attrs['all-clearable']
        : this.clearable;
    },
    computedIconName() {
      return this.clearIconName || xpcConfig.get('clearicon.clearIconName');
    },
    computedClearIconStyle() {
      let style = {};
      if (this.computedClearable && this.type !== 'textarea') {
        style['marginRight'] =
          this.clearIconRight ||
          xpcConfig.get('clearicon.clearIconRight') + 'px';
      }
      if (this.type === 'textarea') {
        style.display = 'flex';
        style.alignItems = 'center';
        if (this.clearIconRight) {
          style.right = this.clearIconRight;
        }
      }
      return style;
    },
    computedDisabled() {
      return this.disabled || (this.elForm || {}).disabled;
    },
    showClose() {
      this.getClearIconRight();
      return (
        this.value &&
        this.inputHovering &&
        !this.computedDisabled &&
        this.computedClearable
      );
    },
    // 兼容APaaS中maxlength属性
    _computedMaxLength() {
      return (this.$attrs && this.$attrs.maxlength) || this.maxLength;
    }
  },
  watch: {
    value(newValue) {
      this.currentValue = newValue;
      this._maxLengthValue();
      let currentDom = this.$refs.input.$el;
      let scrollTop = currentDom.firstChild.scrollTop;
      this.scrollTop = scrollTop;
      this.getClearIconRight();
      this.$nextTick(() => {
        const input = this.$refs.input.getInput();
        this.isTip = input.offsetWidth + 10 < input.scrollWidth;
      });
      if (
        newValue !== undefined &&
        newValue !== null &&
        newValue !== '' &&
        this.elForm &&
        this.elForm.validateField &&
        this.rtFormItem &&
        this.rtFormItem.prop
      ) {
        this.elForm.validateField(this.rtFormItem.prop);
      }
    }
  },
  mounted() {
    this.getClearIconRight();
    this._maxLengthValue();
  },
  methods: {
    /**
     * @private 不允许超出最大长度的 value
     */
    _maxLengthValue() {
      if (
        this.maxLength &&
        this.currentValue &&
        this.currentValue.length > this.maxLength
      ) {
        this.currentValue = this.currentValue.slice(0, this.maxLength);
      }
    },
    getClearIconRight() {
      this.$nextTick(() => {
        if (this.computedClearable) {
          // 判断获取到的节点是否是input类型只有是input类型加paddingRight是div类型的不加
          if (
            this.$refs.input.$el.children[0].className === 'el-input__inner'
          ) {
            this.$refs.input.$el.children[0].style.paddingRight = `${this
              .clearIconRight + 24}px`;
            if (this.type === 'textarea') {
              this.$refs.input.$el.children[0].style.paddingRight = '20px';
            }
          }
        }
      });
    },
    clear() {
      this.currentValue = '';
      this.$emit('input', this.currentValue);
      this.$emit('click');
    },
    inputHandler(newValue = '') {
      this.currentValue = newValue;
      this.$emitDebounce('input', newValue, null, this.datafieldname);
    },
    focus() {
      this.getClearIconRight();
      this.$emit('focus');
    },
    blurHandler() {
      const currentValue =
        this.currentValue === undefined || this.currentValue === null
          ? ''
          : this.currentValue;
      const value =
        this.autoTrim && !this.showPassword
          ? currentValue.trim()
          : currentValue;
      this.inputHandler(value);
      this.$emit('blur', value, null, this.datafieldname);
    },
    changeHandler(newValue = '') {
      const val = newValue === null ? '' : newValue;
      let value = this.autoTrim && !this.showPassword ? val.trim() : val;
      if (this.maxLength) {
        value = value.substring(0, this.maxLength);
      }
      this.$emit('change', value, null, this.datafieldname);
    }
  }
};
</script>



// WEBPACK FOOTER //
// rtXpcInput.vue?4ddf4edc